import React from "react";
import { Lock, LockOpen, Pencil, Database } from "lucide-react";
import { coverageFor, fmtPct } from "@/components/ripeness/value/valuePatch";

export const INPUT = "w-full rounded bg-slate-950/60 border border-slate-700 px-2 py-1 text-xs text-slate-100 font-mono focus:border-emerald-400/60 focus:outline-none";

/** Small chip: what calibration says for one row (CTA count + blend weight), or manual / locked. */
export function CalChip({ cal, lockKey, locked, testId }) {
    const rows = coverageFor(cal, lockKey);
    const best = rows.filter((r) => r.qualifies).sort((a, b) => b.n - a.n)[0];
    const anyN = rows.reduce((s, r) => s + (r.n || 0), 0);
    if (locked) {
        return <span className="inline-flex items-center gap-1 rounded border border-amber-400/40 text-amber-300 px-1.5 py-0.5 text-[10px]" title="Locked — the manual value always wins" data-testid={testId}><Lock className="w-3 h-3" /> locked</span>;
    }
    if (best) {
        const ph = best.phase && best.level !== "phase" ? ` · P${best.phase}` : "";
        return <span className="inline-flex items-center gap-1 rounded border border-violet-400/40 text-violet-300 px-1.5 py-0.5 text-[10px]" title={`${best.n} CTA budgets qualify (k=${cal.k}) — ${fmtPct(best.weight)} of the value comes from data`} data-testid={testId}><Database className="w-3 h-3" /> from {best.n} CTAs{ph} · {fmtPct(best.weight)}</span>;
    }
    if (anyN) {
        return <span className="inline-flex items-center gap-1 rounded border border-slate-600 text-slate-400 px-1.5 py-0.5 text-[10px]" title={`${anyN} CTA budget${anyN > 1 ? "s" : ""} so far — below k=${cal?.k}, manual range in use`} data-testid={testId}><Pencil className="w-3 h-3" /> manual · {anyN}/{cal?.k} CTAs</span>;
    }
    return <span className="inline-flex items-center gap-1 rounded border border-slate-700 text-slate-500 px-1.5 py-0.5 text-[10px]" title="No CTA budgets yet — manual value" data-testid={testId}><Pencil className="w-3 h-3" /> manual</span>;
}

/** Lock toggle for one calibratable row. */
export function LockToggle({ locked, onToggle, testId }) {
    return (
        <button type="button" onClick={onToggle} title={locked ? "Locked — click to release (CTA data blends in)" : "Click to lock (manual value always wins)"}
            className={`inline-flex items-center justify-center w-6 h-6 rounded border ${locked ? "border-amber-400/50 text-amber-300 bg-amber-400/10" : "border-slate-700 text-slate-500 hover:text-slate-200"}`} data-testid={testId}>
            {locked ? <Lock className="w-3 h-3" /> : <LockOpen className="w-3 h-3" />}
        </button>
    );
}

/** Numeric input bound to a draft path; amber border = this set overrides the inherited value. */
export function NumField({ value, onChange, overridden, step = 1, min, max, testId, className = "", placeholder }) {
    const empty = value === null || value === undefined || value === "";
    return (
        <input type="number" step={step} min={min} max={max} value={empty ? "" : value} onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder !== undefined && placeholder !== null ? String(placeholder) : undefined}
            className={`${INPUT} ${overridden ? "border-amber-400/50" : ""} ${empty && placeholder !== undefined ? "placeholder:text-slate-500 italic" : ""} ${className}`}
            title={overridden ? "overridden in this set" : empty && placeholder !== undefined ? `inherited: ${placeholder}` : "inherited"} data-testid={testId} />
    );
}

/** ↺ reset-to-inherited for one overridden field. */
export function ResetBtn({ onClick, testId, title = "Reset to the inherited value" }) {
    return <button type="button" onClick={onClick} className="text-[10px] text-amber-300/80 hover:text-amber-200 px-1" title={title} data-testid={testId}>↺</button>;
}
